import { Component, type ErrorInfo, type ReactNode } from "react";

import { EmptyState } from "./EmptyState";
import { Motion } from "./presence";
import { Button } from "./ui";
import { t } from "../i18n/i18n";
import { recordError } from "../feedback/errorLog";

interface Props {
  children: ReactNode;
  /** Zone concernée (« l'ordre d'opérations »…) ; sans elle, l'application entière. */
  area?: string;
  /** Change de valeur quand l'utilisateur change de vue ou de dossier : l'affichage est retenté. */
  resetKey?: unknown;
  className?: string;
}

interface State {
  error: Error | null;
}

/** Incident d'affichage : message et reprise au lieu d'un écran blanc (carte d'erreur rendue par `EmptyState
 *  variant="error"`, entrée en léger zoom). Les données du dossier sont sur le
 *  serveur, enregistrées pendant la frappe ; recharger ne perd rien. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Incident d'affichage", error, info.componentStack);
    // Jointe au contexte d'une anomalie signalée ensuite (registre F39).
    recordError(error, this.props.area ? "affichage" : "application");
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const { area, className } = this.props;
    return (
      <div className={className ?? "flex h-full w-full items-center justify-center bg-canvas p-6"}>
        <Motion
            origin="zoom"
            className="pointer-events-auto w-[420px] max-w-full rounded-xl border border-line bg-surface shadow-float"
          >
            <EmptyState
              variant="error"
              compact
              title={area ? t("L'affichage a rencontré un incident : {area}", { area: t(area) }) : t("L'application a rencontré un incident")}
              action={<Button onClick={() => window.location.reload()}>{t("Recharger la page")}</Button>}
              secondary={
                area ? (
                  <Button variant="secondary" onClick={() => this.setState({ error: null })}>
                    {t("Réessayer")}
                  </Button>
                ) : undefined
              }
              note={
                <span className="block max-w-[340px] truncate font-mono" title={error.message}>
                  {error.message}
                </span>
              }
            >
              {t("Vos données sont enregistrées sur le serveur : recharger la page ne fait rien perdre.")}
            </EmptyState>
        </Motion>
      </div>
    );
  }
}
